/**
 * The video panel for the selected aircraft.
 *
 * The awkward truth this panel is built around: what a drone actually emits —
 * RTSP, or raw H.264 over UDP — cannot be played by any browser, and HLS only
 * plays natively in Safari. There is no flag that changes either fact.
 *
 * So this never shows a player it cannot drive. Every path either produces a
 * picture or says, in words, what is missing and what would fix it. RTSP plays
 * through the deployment's video gateway when it has one (video/GatewayPlayer). A spinner
 * that never resolves is the one outcome worth engineering against: an
 * operator waits on it, and waiting on a feed that was never coming is worse
 * than knowing there is no feed.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  deleteVideoSource,
  discoverVideoStreams,
  getVideoSource,
  listLanCameras,
  saveVideoSource,
  type DiscoveredStream,
  type LanCamera,
  type VideoSource,
} from '@/api/video';
import { useCanControl } from '@/stores/authStore';
import type { DroneId } from '@/types/domain';

import { GatewayPlayer, LiveBadge } from './video/GatewayPlayer';
import { useFrameWatch, videoFrames } from './video/liveFrames';
import { RecordingPanel } from './video/RecordingPanel';
import { connectWhep } from './video/whep';

/** Whether this browser can play an HLS playlist without a helper library. */
function playsHlsNatively(): boolean {
  const probe = document.createElement('video');
  return probe.canPlayType('application/vnd.apple.mpegurl') !== '';
}

function isHlsPlaylist(url: string): boolean {
  return url.split('?')[0]?.endsWith('.m3u8') ?? false;
}

function Player({ source }: { source: VideoSource }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  const decoded = useRef<() => number | null>(() => null);
  const live = useFrameWatch(() => decoded.current() ?? videoFrames(videoRef.current), {
    failed: failure !== null,
    waiting,
    resetKey: source.url,
  });

  useEffect(() => {
    const video = videoRef.current;
    decoded.current = () => null;
    if (!video || source.kind !== 'webrtc') return;

    let close: (() => void) | null = null;
    let cancelled = false;
    connectWhep(source.url, video)
      .then((session) => {
        if (cancelled) {
          session.close();
          return;
        }
        close = session.close;
        decoded.current = session.framesDecoded;
      })
      .catch((error: unknown) => {
        if (!cancelled) setFailure(error instanceof Error ? error.message : 'Connection failed.');
      });

    return () => {
      cancelled = true;
      close?.();
    };
  }, [source.kind, source.url]);

  if (failure) {
    return <Notice tone="danger" title="NO PICTURE" body={failure} />;
  }

  // Chrome and Firefox cannot open an HLS playlist without a helper library.
  // Saying so beats a black rectangle the operator keeps clicking.
  if (source.kind === 'hls' && isHlsPlaylist(source.url) && !playsHlsNatively()) {
    return (
      <Notice
        tone="warn"
        title="NOT PLAYABLE HERE"
        body="This browser cannot open an HLS playlist. Safari plays it as-is; elsewhere the feed needs to be served as WebRTC or a progressive file."
      />
    );
  }

  return (
    <div className="relative">
      <video
        ref={videoRef}
        className="max-h-[60dvh] w-full rounded-[8px] bg-black object-contain"
        src={source.kind === 'webrtc' ? undefined : source.url}
        autoPlay
        muted
        playsInline
        controls
        aria-label={`${source.label} video`}
        onError={() => setFailure('The stream could not be opened.')}
        onWaiting={() => setWaiting(true)}
        onStalled={() => setWaiting(true)}
        onPlaying={() => setWaiting(false)}
      />
      <LiveBadge state={live} />
    </div>
  );
}

function Notice({ tone, title, body }: { tone: 'warn' | 'danger'; title: string; body: string }) {
  const color = tone === 'danger' ? 'var(--danger-foreground)' : 'var(--warn)';
  return (
    <div className="well flex flex-col gap-1 rounded-[11px] px-3 py-2.5">
      <span className="label-mono text-[8px]" style={{ color }}>
        {title}
      </span>
      <p className="text-[11px] leading-snug text-[color:var(--muted-foreground-2)]">{body}</p>
    </div>
  );
}

/** A feed label the server accepts, made from whatever name a camera gave itself. */
function feedLabel(name: string): string {
  const cleaned = name
    .toUpperCase()
    .replace(/[^A-Z0-9 ._\-/#]/g, '')
    .trim()
    .slice(0, 48);
  return cleaned || 'CAMERA';
}

function SourceForm({ droneId, onSaved }: { droneId: DroneId; onSaved: () => void }) {
  const [label, setLabel] = useState('NOSE');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [found, setFound] = useState<DiscoveredStream[]>([]);
  const [heard, setHeard] = useState<string | null>(null);

  const [lan, setLan] = useState<LanCamera[]>([]);

  // Cameras the connector on the operator's laptop found on the radio LAN.
  // Shown back only to the operator who ran the sweep, and only as a way to
  // fill the form: a found camera is a suggestion, not a saved feed.
  useEffect(() => {
    let cancelled = false;
    void listLanCameras().then((result) => {
      if (!cancelled && result.ok) setLan(result.data.cameras);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const fillFrom = (stream: DiscoveredStream) => {
    setLabel(stream.label);
    setUrl(stream.url);
    setError(null);
  };

  const fillFromLan = (camera: LanCamera) => {
    setLabel(feedLabel(camera.name));
    setUrl(camera.url);
    setError(null);
  };

  // The camera knows its own stream address better than a label on the
  // airframe does. Its answer fills the form and nothing more: saving still
  // goes through SET FEED and the same checks as a typed URL.
  const ask = async () => {
    setAsking(true);
    setHeard(null);
    setFound([]);
    const response = await discoverVideoStreams(droneId);
    setAsking(false);
    if (!response.ok) {
      setHeard(response.error.message);
      return;
    }
    const { result, streams } = response.data;
    setFound(streams);
    setHeard(result.message);
    if (streams[0]) fillFrom(streams[0]);
  };

  const submit = async () => {
    setBusy(true);
    const result = await saveVideoSource(droneId, { label, url });
    setBusy(false);
    if (result.ok) {
      setUrl('');
      onSaved();
    } else {
      // The server's message names what is wrong with the URL. Replacing it
      // with "invalid" would throw away the only useful part.
      setError(result.error.message);
    }
  };

  return (
    <div className="well flex flex-col gap-2 rounded-[11px] px-3 py-2.5">
      <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
        NO FEED CONFIGURED
      </span>
      <label className="flex flex-col gap-1">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">LABEL</span>
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          className="well rounded-[7px] px-2 py-1 font-mono text-[11px] outline-none"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
          STREAM URL
        </span>
        <input
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://… or webrtc://…"
          className="well rounded-[7px] px-2 py-1 font-mono text-[11px] outline-none"
        />
      </label>
      <button
        type="button"
        disabled={asking}
        onClick={() => void ask()}
        className="well rounded-[99px] px-2 py-1 label-mono text-[9px] disabled:opacity-40"
      >
        {asking ? 'ASKING THE CAMERA…' : 'ASK THE AIRCRAFT'}
      </button>
      {heard ? (
        <p className="text-[10px] leading-snug text-[color:var(--muted-foreground-2)]">{heard}</p>
      ) : null}
      {found.length > 1 ? (
        <div className="flex flex-col gap-1" role="list" aria-label="Streams the aircraft reported">
          {found.map((stream) => (
            <button
              key={`${stream.cameraComponent}-${stream.streamId}`}
              type="button"
              role="listitem"
              onClick={() => fillFrom(stream)}
              className="well rounded-[7px] px-2 py-1 text-left font-mono text-[10px]"
            >
              {stream.label} {stream.redactedUrl}
            </button>
          ))}
        </div>
      ) : null}
      {lan.length > 0 ? (
        <div className="flex flex-col gap-1" role="list" aria-label="Cameras found on the radio LAN">
          <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
            FOUND ON THE RADIO LAN
          </span>
          {lan.map((camera) => (
            <button
              key={camera.url}
              type="button"
              role="listitem"
              onClick={() => fillFromLan(camera)}
              className="well rounded-[7px] px-2 py-1 text-left font-mono text-[10px]"
            >
              {camera.name} {camera.url}
              {camera.viaGateway ? null : (
                <span className="block text-[9px] text-[color:var(--muted-foreground-2)]">
                  Plain HTTP on the radio LAN. An HTTPS page cannot show it, and the gateway
                  restreams RTSP only.
                </span>
              )}
            </button>
          ))}
        </div>
      ) : null}
      {found.some((stream) => stream.url === url && stream.hasCredentials) ? (
        <p className="text-[9px] leading-snug text-[color:var(--muted-foreground-2)]">
          The camera's address carries a password. It is filled in here so the feed can be saved,
          and hidden in listings after that.
        </p>
      ) : null}
      {error ? (
        <p className="text-[10px] leading-snug text-[color:var(--danger-foreground)]">{error}</p>
      ) : null}
      <button
        type="button"
        disabled={busy || !url.trim()}
        onClick={() => void submit()}
        className="well rounded-[99px] px-2 py-1 label-mono text-[9px] disabled:opacity-40"
      >
        {busy ? 'SAVING…' : 'SET FEED'}
      </button>
    </div>
  );
}

export function VideoTab({ droneId }: { droneId: DroneId | null }) {
  const [source, setSource] = useState<VideoSource | null>(null);
  const [missing, setMissing] = useState(false);
  const canRecord = useCanControl(droneId ?? ('' as DroneId));

  const load = useCallback(() => {
    if (!droneId) return;
    void getVideoSource(droneId).then((result) => {
      setSource(result.ok ? result.data : null);
      setMissing(!result.ok);
    });
  }, [droneId]);

  useEffect(load, [load]);

  if (!droneId) {
    return (
      <div className="well rounded-[11px] px-3 py-2.5">
        <p className="text-[11px] text-[color:var(--muted-foreground-2)]">
          Select an aircraft to see its feed.
        </p>
      </div>
    );
  }

  if (missing || !source) {
    return <SourceForm droneId={droneId} onSaved={load} />;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
          {source.label}
        </span>
        <span className="label-mono ml-auto text-[8px] text-[color:var(--muted-foreground-2)]">
          {source.kind.toUpperCase()}
        </span>
        <button
          type="button"
          onClick={() => void deleteVideoSource(droneId).then(load)}
          className="label-mono text-[8px] text-[color:var(--muted-foreground-2)] hover:text-[color:var(--danger-foreground)]"
        >
          CLEAR
        </button>
      </div>

      {source.playable === 'native' ? (
        <Player source={source} />
      ) : source.viaGateway ? (
        <GatewayPlayer source={source} />
      ) : (
        <Notice
          tone="warn"
          title="NEEDS A GATEWAY"
          body={
            source.gateway?.enabled === false && source.kind === 'rtsp'
              ? `${source.note} This server's video gateway is not configured (VIDEO_GATEWAY_SECRET).`
              : source.note
          }
        />
      )}

      {source.viaGateway ? <RecordingPanel droneId={droneId} canRecord={canRecord} /> : null}

      {source.hasCredentials && source.viaGateway ? (
        <p className="text-[9px] leading-snug text-[color:var(--muted-foreground-2)]">
          The camera's password stays on the server. The gateway connects to the camera; this
          browser connects only to the gateway.
        </p>
      ) : source.hasCredentials ? (
        <p className="text-[9px] leading-snug text-[color:var(--muted-foreground-2)]">
          This URL carries credentials. They are hidden in listings but sent to this browser to
          play the stream.
        </p>
      ) : null}
    </div>
  );
}
